import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Pencil, Trash2, ChevronDown } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import Drawer from '../components/Drawer';
import ParkingFloorFormSlider from '../components/ParkingFloorFormSlider';
import ParkingAddonFormSlider from '../components/ParkingAddonFormSlider';
import ParkingTypeFormSlider from '../components/ParkingTypeFormSlider';
import ParkingFloorDetailsSlider from '../components/ParkingFloorDetailsSlider';
import { parkingFloorService, parkingAddonService, parkingTypeService } from '../services/parkingService';
import { ParkingFloor, ParkingAddon, ParkingType } from '../types/parking';
import api from '../services/api';
import '../styles/Builders.css';
import '../styles/Buttons.css';

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface Project {
  _id: string;
  name: string;
}

const Parking: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [parkingFloors, setParkingFloors] = useState<ParkingFloor[]>([]);
  const [parkingAddons, setParkingAddons] = useState<ParkingAddon[]>([]);
  const [parkingTypes, setParkingTypes] = useState<ParkingType[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selectedProject, setSelectedProject] = useState('');
  const [selectedParkingFloorId, setSelectedParkingFloorId] = useState<string | null>(null);

  // Delete modals state
  const [showDeleteFloorModal, setShowDeleteFloorModal] = useState(false);
  const [floorToDelete, setFloorToDelete] = useState<ParkingFloor | null>(null);
  const [showDeleteAddonModal, setShowDeleteAddonModal] = useState(false);
  const [addonToDelete, setAddonToDelete] = useState<ParkingAddon | null>(null);
  const [showDeleteTypeModal, setShowDeleteTypeModal] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState<ParkingType | null>(null);

  // Get drawer state from URL params
  const isFloorDrawerOpen = searchParams.get('action') === 'new-floor' || (!!searchParams.get('edit') && searchParams.get('type') === 'floor');
  const editFloorId = searchParams.get('type') === 'floor' ? searchParams.get('edit') : null;

  const isAddonDrawerOpen = searchParams.get('action') === 'new-addon' || (!!searchParams.get('edit') && searchParams.get('type') === 'addon');
  const editAddonId = searchParams.get('type') === 'addon' ? searchParams.get('edit') : null;

  const isTypeDrawerOpen = searchParams.get('action') === 'new-type' || (!!searchParams.get('edit') && searchParams.get('type') === 'type');
  const editTypeId = searchParams.get('type') === 'type' ? searchParams.get('edit') : null;

  useEffect(() => {
    if (workspaceId) {
      fetchProjects();
      fetchAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, selectedProject]);

  const fetchProjects = async () => {
    if (!workspaceId) return;

    try {
      const response = await api.get(`/workspaces/${workspaceId}/projects`, {
        params: {
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      setProjects(response.data.projects);

      // Automatically select the first project if no project is selected
      if (response.data.projects.length > 0 && !selectedProject) {
        setSelectedProject(response.data.projects[0]._id);
      }
    } catch (err: any) {
      console.error('Error fetching projects:', err);
    }
  };

  const fetchAllData = async () => {
    if (!workspaceId || !selectedProject) return;

    try {
      setLoading(true);
      setError('');

      const params: any = {
        limit: 50,
        sortBy: 'name',
        sortOrder: 'asc',
        projectId: selectedProject
      };

      const [floorsResponse, addonsResponse, typesResponse] = await Promise.all([
        parkingFloorService.getAll(workspaceId, params),
        parkingAddonService.getAll(workspaceId, params),
        parkingTypeService.getAll(workspaceId, params)
      ]);

      setParkingFloors(floorsResponse.parkingFloors);
      setParkingAddons(addonsResponse.parkingAddons);
      setParkingTypes(typesResponse.parkingTypes);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch parking data');
      console.error('Error fetching parking data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToWorkspaces = () => {
    navigate('/workspaces');
  };

  const handleAddFloor = () => {
    setSearchParams({ action: 'new-floor' });
  };

  const handleAddAddon = () => {
    setSearchParams({ action: 'new-addon' });
  };

  const handleAddType = () => {
    setSearchParams({ action: 'new-type' });
  };

  const handleEditFloor = (id: string) => {
    setSearchParams({ edit: id, type: 'floor' });
  };

  const handleEditAddon = (id: string) => {
    setSearchParams({ edit: id, type: 'addon' });
  };

  const handleEditType = (id: string) => {
    setSearchParams({ edit: id, type: 'type' });
  };

  const handleCloseFloorDrawer = () => {
    setSearchParams({});
  };

  const handleFloorSuccess = () => {
    setSearchParams({});
    fetchAllData();
  };

  const handleCloseAddonDrawer = () => {
    setSearchParams({});
  };

  const handleAddonSuccess = () => {
    setSearchParams({});
    fetchAllData();
  };

  const handleCloseTypeDrawer = () => {
    setSearchParams({});
  };

  const handleTypeSuccess = () => {
    setSearchParams({});
    fetchAllData();
  };

  const handleDeleteFloorClick = (floor: ParkingFloor) => {
    setFloorToDelete(floor);
    setShowDeleteFloorModal(true);
  };

  const handleDeleteFloorConfirm = async () => {
    if (!floorToDelete || !workspaceId) return;

    try {
      await parkingFloorService.delete(workspaceId, floorToDelete._id);
      setShowDeleteFloorModal(false);
      setFloorToDelete(null);
      fetchAllData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete parking floor');
      console.error('Error deleting parking floor:', err);
    }
  };

  const handleDeleteAddonClick = (addon: ParkingAddon) => {
    setAddonToDelete(addon);
    setShowDeleteAddonModal(true);
  };

  const handleDeleteAddonConfirm = async () => {
    if (!addonToDelete || !workspaceId) return;

    try {
      await parkingAddonService.delete(workspaceId, addonToDelete._id);
      setShowDeleteAddonModal(false);
      setAddonToDelete(null);
      fetchAllData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete parking add-on');
      console.error('Error deleting parking add-on:', err);
    }
  };

  const handleDeleteTypeClick = (type: ParkingType) => {
    setTypeToDelete(type);
    setShowDeleteTypeModal(true);
  };

  const handleDeleteTypeConfirm = async () => {
    if (!typeToDelete || !workspaceId) return;

    try {
      await parkingTypeService.delete(workspaceId, typeToDelete._id);
      setShowDeleteTypeModal(false);
      setTypeToDelete(null);
      fetchAllData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete parking type');
      console.error('Error deleting parking type:', err);
    }
  };

  const handleFloorRowClick = (floorId: string) => {
    setSelectedParkingFloorId(floorId);
  };

  const handleCloseParkingFloorDetails = () => {
    setSelectedParkingFloorId(null);
  };

  if (!workspaceId) {
    return (
      <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
        <div className="builders-container">
          <div className="builders-error">
            <p>No workspace selected</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
      <>
        <div className="builders-container">
          {/* Section Header */}
          <div className="builders-section-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 className="builders-section-title">Parking</h2>
                <p className="builders-section-subtitle">
                  Manage parking floors, add-ons, and types
                </p>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <select
                  className="form-input"
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  style={{ width: '250px' }}
                >
                  {projects.map((project) => (
                    <option key={project._id} value={project._id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="builders-error" style={{ marginBottom: '20px' }}>
              <p>{error}</p>
            </div>
          )}
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="builders-container">
            <div className="builders-loading">
              <div className="builders-loading-spinner" />
            </div>
          </div>
        ) : (
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 2px 8px rgba(72, 24, 47, 0.08)'
          }}>
            {/* Parking Floors Table */}
            <div style={{ marginBottom: '40px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0, color: '#48182F' }}>Parking Floors</h3>
                <button className="btn btn-primary btn-sm" onClick={handleAddFloor}>
                  Add Floor
                </button>
              </div>

              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Project</th>
                      <th>Name</th>
                      <th>Total</th>
                      <th>Available</th>
                      <th>Marketing Hold</th>
                      <th>Sales Hold</th>
                      <th>Sold</th>
                      <th>Customer Selection</th>
                      <th>Customer Submitted</th>
                      <th style={{ width: '100px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parkingFloors.length === 0 ? (
                      <tr>
                        <td colSpan={10} style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>
                          No parking floors found. Click "Add" to create one.
                        </td>
                      </tr>
                    ) : (
                      parkingFloors.map((floor) => (
                        <tr
                          key={floor._id}
                          onClick={() => handleFloorRowClick(floor._id)}
                        >
                          <td><strong>{floor.projectName || '-'}</strong></td>
                          <td><strong>{floor.name}</strong></td>
                          <td>{floor.total}</td>
                          <td>{floor.available}</td>
                          <td>{floor.marketingHold}</td>
                          <td>{floor.salesHold}</td>
                          <td>{floor.sold}</td>
                          <td>{floor.customerSelection}</td>
                          <td>{floor.customerSubmitted}</td>
                          <td>
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                              <button
                                className="btn-icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditFloor(floor._id);
                                }}
                                title="Edit"
                              >
                                <Pencil size={16} />
                              </button>
                              <button
                                className="btn-icon btn-icon-danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteFloorClick(floor);
                                }}
                                title="Delete"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Parking Add-ons Table */}
            <div style={{ marginBottom: '40px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0, color: '#48182F' }}>Parking Add-ons</h3>
                <button className="btn btn-primary btn-sm" onClick={handleAddAddon}>
                  Add Add-on
                </button>
              </div>

              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Project</th>
                      <th>Name</th>
                      <th>Total</th>
                      <th>Available</th>
                      <th>Capable</th>
                      <th>Sold</th>
                      <th>Cost</th>
                      <th>Price</th>
                      <th style={{ width: '100px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parkingAddons.length === 0 ? (
                      <tr>
                        <td colSpan={9} style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>
                          No parking add-ons found. Click "Add" to create one.
                        </td>
                      </tr>
                    ) : (
                      parkingAddons.map((addon) => (
                        <tr key={addon._id}>
                          <td><strong>{addon.projectName || '-'}</strong></td>
                          <td><strong>{addon.name}</strong></td>
                          <td>{addon.total}</td>
                          <td>{addon.available}</td>
                          <td>{addon.capable}</td>
                          <td>{addon.sold}</td>
                          <td>{addon.cost != null ? `$${addon.cost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}</td>
                          <td>{addon.price != null ? `$${addon.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}</td>
                          <td>
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                              <button
                                className="btn-icon"
                                onClick={() => handleEditAddon(addon._id)}
                                title="Edit"
                              >
                                <Pencil size={16} />
                              </button>
                              <button
                                className="btn-icon btn-icon-danger"
                                onClick={() => handleDeleteAddonClick(addon)}
                                title="Delete"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Parking Types Table */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0, color: '#48182F' }}>Parking Types</h3>
                <button className="btn btn-primary btn-sm" onClick={handleAddType}>
                  Add Type
                </button>
              </div>

              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Project</th>
                      <th>Name</th>
                      <th>Total</th>
                      <th>Available</th>
                      <th>Marketing Hold</th>
                      <th>Sales Hold</th>
                      <th>Sold</th>
                      <th>Customer Selection</th>
                      <th>Customer Submitted</th>
                      <th style={{ width: '100px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parkingTypes.length === 0 ? (
                      <tr>
                        <td colSpan={10} style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>
                          No parking types found. Click "Add" to create one.
                        </td>
                      </tr>
                    ) : (
                      parkingTypes.map((type) => (
                        <tr key={type._id}>
                          <td><strong>{type.projectName || '-'}</strong></td>
                          <td><strong>{type.name}</strong></td>
                          <td>{type.total}</td>
                          <td>{type.available}</td>
                          <td>{type.marketingHold}</td>
                          <td>{type.salesHold}</td>
                          <td>{type.sold}</td>
                          <td>{type.customerSelection}</td>
                          <td>{type.customerSubmitted}</td>
                          <td>
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                              <button
                                className="btn-icon"
                                onClick={() => handleEditType(type._id)}
                                title="Edit"
                              >
                                <Pencil size={16} />
                              </button>
                              <button
                                className="btn-icon btn-icon-danger"
                                onClick={() => handleDeleteTypeClick(type)}
                                title="Delete"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Drawer for Parking Floor Create/Edit */}
        <Drawer
          isOpen={isFloorDrawerOpen}
          onClose={handleCloseFloorDrawer}
          title={editFloorId ? 'Edit Parking Floor' : 'Add Parking Floor'}
          footer={
            <div className="drawer-footer-actions">
              <button type="button" className="btn btn-secondary btn-md" onClick={handleCloseFloorDrawer} form="parking-floor-form">
                Cancel
              </button>
              <button type="submit" className="btn btn-primary btn-md" form="parking-floor-form">
                {editFloorId ? 'Update' : 'Add'}
              </button>
            </div>
          }
        >
          {isFloorDrawerOpen && (
            <ParkingFloorFormSlider
              workspaceId={workspaceId!}
              floorId={editFloorId || undefined}
              onSuccess={handleFloorSuccess}
              onCancel={handleCloseFloorDrawer}
            />
          )}
        </Drawer>

        {/* Drawer for Parking Add-on Create/Edit */}
        <Drawer
          isOpen={isAddonDrawerOpen}
          onClose={handleCloseAddonDrawer}
          title={editAddonId ? 'Edit Parking Add-on' : 'Add Parking Add-on'}
          footer={
            <div className="drawer-footer-actions">
              <button type="button" className="btn btn-secondary btn-md" onClick={handleCloseAddonDrawer} form="parking-addon-form">
                Cancel
              </button>
              <button type="submit" className="btn btn-primary btn-md" form="parking-addon-form">
                {editAddonId ? 'Update' : 'Add'}
              </button>
            </div>
          }
        >
          {isAddonDrawerOpen && (
            <ParkingAddonFormSlider
              workspaceId={workspaceId!}
              addonId={editAddonId || undefined}
              onSuccess={handleAddonSuccess}
              onCancel={handleCloseAddonDrawer}
            />
          )}
        </Drawer>

        {/* Delete Confirmation Modal - Parking Floor */}
        {showDeleteFloorModal && (
          <div className="builders-modal-overlay" onClick={() => setShowDeleteFloorModal(false)}>
            <div className="builders-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Delete Parking Floor</h3>
              <p>Are you sure you want to delete "{floorToDelete?.name}"?</p>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: '10px' }}>
                This action cannot be undone.
              </p>
              <div className="builders-modal-actions">
                <button className="btn btn-secondary btn-md" onClick={() => setShowDeleteFloorModal(false)}>
                  Cancel
                </button>
                <button className="btn btn-danger btn-md" onClick={handleDeleteFloorConfirm}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal - Parking Add-on */}
        {showDeleteAddonModal && (
          <div className="builders-modal-overlay" onClick={() => setShowDeleteAddonModal(false)}>
            <div className="builders-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Delete Parking Add-on</h3>
              <p>Are you sure you want to delete "{addonToDelete?.name}"?</p>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: '10px' }}>
                This action cannot be undone.
              </p>
              <div className="builders-modal-actions">
                <button className="btn btn-secondary btn-md" onClick={() => setShowDeleteAddonModal(false)}>
                  Cancel
                </button>
                <button className="btn btn-danger btn-md" onClick={handleDeleteAddonConfirm}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal - Parking Type */}
        {showDeleteTypeModal && (
          <div className="builders-modal-overlay" onClick={() => setShowDeleteTypeModal(false)}>
            <div className="builders-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Delete Parking Type</h3>
              <p>Are you sure you want to delete "{typeToDelete?.name}"?</p>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: '10px' }}>
                This action cannot be undone.
              </p>
              <div className="builders-modal-actions">
                <button className="btn btn-secondary btn-md" onClick={() => setShowDeleteTypeModal(false)}>
                  Cancel
                </button>
                <button className="btn btn-danger btn-md" onClick={handleDeleteTypeConfirm}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Drawer for Parking Type Create/Edit */}
        <Drawer
          isOpen={isTypeDrawerOpen}
          onClose={handleCloseTypeDrawer}
          title={editTypeId ? 'Edit Parking Type' : 'Add Parking Type'}
          footer={
            <div className="drawer-footer-actions">
              <button type="button" className="btn btn-secondary btn-md" onClick={handleCloseTypeDrawer} form="parking-type-form">
                Cancel
              </button>
              <button type="submit" className="btn btn-primary btn-md" form="parking-type-form">
                {editTypeId ? 'Update' : 'Add'}
              </button>
            </div>
          }
        >
          {isTypeDrawerOpen && (
            <ParkingTypeFormSlider
              workspaceId={workspaceId!}
              typeId={editTypeId || undefined}
              onSuccess={handleTypeSuccess}
              onCancel={handleCloseTypeDrawer}
            />
          )}
        </Drawer>

        {/* Parking Floor Details Slider */}
        {selectedParkingFloorId && (
          <ParkingFloorDetailsSlider
            workspaceId={workspaceId!}
            parkingFloorId={selectedParkingFloorId}
            onClose={handleCloseParkingFloorDetails}
          />
        )}
      </>
    </AppLayout>
  );
};

export default Parking;
